import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/CameraResults.css';

const API_BASE = 'https://api.laviopaperie.com';
function joinUrl(base, path) {
  if (!base.endsWith('/') && !path.startsWith('/')) return base + '/' + path;
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
  return base + path;
}

const CameraResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [maxSelect, setMaxSelect] = useState(6);
  const [showSelection, setShowSelection] = useState(true);
  const [mirroringFiles, setMirroringFiles] = useState(new Set());
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');

  // Move photoFiles and videoFiles here so all hooks can use them
  const photoFiles = files.filter(file => file.endsWith('.jpg'));
  const videoFiles = files.filter(file => file.endsWith('.webm'));

  // --- Countdown Timer State ---
  const [countdown, setCountdown] = useState(120); // 2 minutes in seconds
  const countdownIntervalRef = React.useRef(null);

  // Load attractive fonts
  useEffect(() => {
    const fontLinks = [
      'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap'
    ];

    fontLinks.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
    
    // Add wave animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);


    return () => {
      // Cleanup fonts on unmount
      document.querySelectorAll('link[href*="fonts.googleapis.com"]').forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, []);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);

        // Fetch session data
        const sessionResponse = await fetch(`${API_BASE}/api/photo-session/${sessionId}`);
        if (!sessionResponse.ok) {
          throw new Error('Session not found');
        }
        const session = await sessionResponse.json();
        setSessionData(session);

        // Fetch files
        const filesResponse = await fetch(`${API_BASE}/api/check-files/${sessionId}`);
        if (!filesResponse.ok) {
          throw new Error('Failed to fetch files');
        }
        const filesData = await filesResponse.json();
        setFiles(filesData.files);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  // Load selected frame to know how many shapes to select
  useEffect(() => {
    const loadFrameCount = async () => {
      try {
        // Ambil frameId dari sessionData.grid
        if (!sessionData || !sessionData.grid) return;
        const frameId = Number(sessionData.grid) || null;
        if (!frameId) return;
        const res = await fetch(`${API_BASE}/api/workspaces/${frameId}`);
        const data = await res.json();
        if (!res.ok) return;
        const shapesCount = (data.items || []).filter((it) => it.item_type === 'shape').length || 0;
        if (shapesCount > 0) setMaxSelect(shapesCount);
      } catch { }
    };
    loadFrameCount();
  }, [sessionData]);

  // Start countdown on mount
  useEffect(() => {
    if (!showSelection) return;
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownIntervalRef.current);
  }, [showSelection]);

  // Auto-select and redirect when countdown ends
  useEffect(() => {
    if (countdown === 0 && showSelection && selectedPhotos.length < maxSelect && photoFiles.length >= maxSelect) {
      // Auto-select first maxSelect photos
      const autoSelected = photoFiles.slice(0, maxSelect);
      setSelectedPhotos(autoSelected);
      setTimeout(() => {
        navigate(`/selected-results/${sessionId}`, { state: { selectedPhotos: autoSelected } });
      }, 800);
    }
  }, [countdown, showSelection, maxSelect, photoFiles, selectedPhotos.length, sessionId, navigate]);

  // Format countdown mm:ss
  const formatCountdown = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  const downloadAll = async () => {
    try {
      setMirroringFiles(new Set(files));
      showToast('Memulai proses mirroring untuk semua file...', 'info');

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      let successCount = 0;
      let fallbackCount = 0;

      // Download all files with mirroring
      const downloadPromises = files.map(async (filename) => {
        try {
          const response = await fetch(joinUrl(API_BASE, `/uploads/${filename}`));
          const blob = await response.blob();

          // Apply mirroring based on file type
          let mirroredBlob = blob;
          try {
            if (filename.endsWith('.jpg')) {
              mirroredBlob = await createMirroredPhoto(blob);
              if (!mirroredBlob || mirroredBlob.size === 0) throw new Error('Empty mirrored image');
              successCount++;
            } else if (filename.endsWith('.webm')) {
              mirroredBlob = await createMirroredVideo(blob);
              if (!mirroredBlob || mirroredBlob.size === 0) throw new Error('Empty mirrored video');
              successCount++;
            }
          } catch (mirrorError) {
            console.warn(`Mirroring failed for ${filename}, using original:`, mirrorError);
            // Use original file if mirroring fails
            mirroredBlob = blob;
            fallbackCount++;
          }

          zip.file(filename, mirroredBlob);

          // Remove from mirroring set when done
          setMirroringFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(filename);
            return newSet;
          });
        } catch (error) {
          console.error(`Error processing ${filename}:`, error);
          // Remove from mirroring set on error
          setMirroringFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(filename);
            return newSet;
          });
        }
      });

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${sessionId}_all_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Clear mirroring state
      setMirroringFiles(new Set());

      // Show success message with counts
      if (fallbackCount > 0) {
        showToast(
          `Download selesai! ${successCount} file berhasil di-mirror, ${fallbackCount} file menggunakan versi asli.`,
          'success'
        );
      } else {
        showToast(`Download selesai! Semua ${successCount} file berhasil di-mirror.`, 'success');
      }

    } catch (error) {
      console.error('Error downloading files:', error);
      showToast('Terjadi kesalahan saat mengunduh file.', 'error');
      setMirroringFiles(new Set());
    }
  };

  const downloadFile = async (filename) => {
    try {
      setMirroringFiles(prev => new Set(prev).add(filename));

      const response = await fetch(joinUrl(API_BASE, `/uploads/${filename}`));
      const blob = await response.blob();

      // Apply mirroring based on file type
      let downloadBlob = blob;
      try {
        if (filename.endsWith('.jpg')) {
          downloadBlob = await createMirroredPhoto(blob);
          showToast(`Foto ${filename} berhasil di-mirror!`, 'success');
        } else if (filename.endsWith('.webm')) {
          const mirrored = await createMirroredVideo(blob);
          if (!mirrored || mirrored.size === 0) {
            showToast(`Mirroring gagal untuk ${filename}, menggunakan file asli`, 'warning');
            downloadBlob = blob; // fallback
          } else {
            showToast(`Video ${filename} berhasil di-mirror!`, 'success');
            downloadBlob = mirrored;
          }
        }
      } catch (mirrorError) {
        console.warn(`Mirroring failed for ${filename}, using original:`, mirrorError);
        // Use original file if mirroring fails
        downloadBlob = blob;
        showToast(`Mirroring gagal untuk ${filename}, menggunakan file asli`, 'warning');
      }

      const downloadUrl = URL.createObjectURL(downloadBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Remove from mirroring set when done
      setMirroringFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filename);
        return newSet;
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast('Terjadi kesalahan saat mengunduh file.', 'error');

      // Remove from mirroring set on error
      setMirroringFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filename);
        return newSet;
      });
    }
  };

  // Create mirrored version of photo
  const createMirroredPhoto = async (photoBlob) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = img.width;
          canvas.height = img.height;

          // Apply mirror effect
          ctx.scale(-1, 1);
          ctx.translate(-canvas.width, 0);
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.9);
        };

        img.onerror = reject;
        img.src = URL.createObjectURL(photoBlob);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Create mirrored version of video - robust, ensures non-zero output
  const createMirroredVideo = async (videoBlob) => {
    return new Promise((resolve, reject) => {
      try {
        const videoUrl = URL.createObjectURL(videoBlob);
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = 'anonymous';

        let mediaRecorder = null;
        let stream = null;
        const chunks = [];
        let frameCount = 0;
        let stopped = false;

        const finalize = () => {
          try {
            URL.revokeObjectURL(videoUrl);
            const type = (mediaRecorder && mediaRecorder.mimeType) || 'video/webm';
            if (chunks.length === 0 || frameCount === 0) {
              reject(new Error('No video data recorded'));
              return;
            }
            const mirroredBlob = new Blob(chunks, { type });
            resolve(mirroredBlob);
          } catch (e) {
            reject(e);
          }
        };

        const stopRecording = () => {
          if (stopped) return;
          stopped = true;
          try {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              // Flush last data and stop shortly after
              mediaRecorder.requestData();
              setTimeout(() => {
                try { mediaRecorder.stop(); } catch (_) { finalize(); }
              }, 80);
            } else {
              finalize();
            }
          } catch (_) {
            finalize();
          }
        };

        video.onloadedmetadata = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            stream = canvas.captureStream(30);

            const mimeCandidates = [
              'video/webm;codecs=vp9',
              'video/webm;codecs=vp8',
              'video/webm'
            ];
            const selected =
              mimeCandidates.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) ||
              'video/webm';

            mediaRecorder = new MediaRecorder(stream, { mimeType: selected });

            mediaRecorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) chunks.push(e.data);
            };
            mediaRecorder.onstop = finalize;
            mediaRecorder.onerror = (ev) => reject(ev.error || new Error('MediaRecorder error'));

            // Start with timeslice to ensure non-empty chunks
            mediaRecorder.start(100);

            const draw = () => {
              if (video.ended) return;
              // Draw only when ready, but do not stop on buffering/pause
              if (video.readyState >= 2 && !video.paused) {
                ctx.save();
                ctx.scale(-1, 1);
                ctx.translate(-canvas.width, 0);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                ctx.restore();
                frameCount++;
              }
              requestAnimationFrame(draw);
            };

            video.onended = stopRecording;

            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.then(() => {
                draw();
              }).catch(err => reject(err));
            } else {
              draw();
            }
          } catch (e) {
            reject(e);
          }
        };

        video.onerror = () => reject(new Error('Failed to load video'));
        video.src = videoUrl;
        video.load();
      } catch (e) {
        reject(e);
      }
    });
  };

  const togglePhotoSelection = (filename) => {
    if (selectedPhotos.includes(filename)) {
      setSelectedPhotos(prev => prev.filter(photo => photo !== filename));
    } else if (selectedPhotos.length < maxSelect) {
      setSelectedPhotos(prev => [...prev, filename]);
    }
  };

  const proceedToSelection = () => {
    if (selectedPhotos.length === maxSelect) {
      navigate(`/selected-results/${sessionId}`, {
        state: { selectedPhotos }
      });
    }
  };

  const goBackToSelection = () => {
    setShowSelection(true);
  };

  const showToast = (message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage('');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="camera-results" style={{ display: 'flex' }}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Memuat hasil session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-results"  style={{ display: 'flex' }}>
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/camera-session')} className="back-button">
            Kembali ke Camera Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-results"  style={{ display: 'flex' }}>
      <div className="results-header" style={{ display: 'none' }}>
        <h1>Hasil Camera Session</h1>
        <div className="session-info">
          <p><strong>Session ID:</strong> {sessionId}</p>
          <p><strong>Total Foto:</strong> {photoFiles.length}</p>
          <p><strong>Total Video:</strong> {videoFiles.length}</p>
          {sessionData && (
            <p><strong>Tanggal:</strong> {new Date(sessionData.created_at).toLocaleString('id-ID')}</p>
          )}
        </div>
      </div>

      <div className="actions" style={{ display: 'none' }}>
        <button
          onClick={downloadAll}
          className="download-all-button"
          disabled={mirroringFiles.size > 0}
        >
          {mirroringFiles.size > 0
            ? `Mirroring ${mirroringFiles.size} files...`
            : 'Download Semua File'
          }
        </button>
        <button onClick={() => navigate('/camera-session')} className="new-session-button">
          Session Baru
        </button>
      </div>

      <div className="results-content">
        {/* Toast Notification */}
        {toastMessage && (
          <div className={`toast-notification ${toastType}`}>
            <span>{toastMessage}</span>
            <button
              onClick={() => setToastMessage('')}
              className="toast-close"
            >
              ×
            </button>
          </div>
        )}

        <div className="photos-section" style={{position:'relative'}}>
          {/* Flex row for Lavio Paperie, Total Photos, and Countdown */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            marginBottom: '10px',
            gap: '18px',
            position: 'relative',
            minHeight: '54px',
          }}>
            {/* Left: Lavio Paperie */}
            <span style={{
              fontFamily: `'Great Vibes', cursive`,
              fontSize: '38px',
              color: '#8f72a6',
              textShadow: '0 1px 0 rgba(255,255,255,0.6)',
              letterSpacing: '1px',
              display: 'inline-block',
              lineHeight: 1.1,
              marginLeft: 0,
              marginTop:'10px',
              marginRight: '18px',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              zIndex: 2,
            }}>
              {Array.from(' - Lavio Paperie - ').map((ch, i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-block',
                    animation: 'titleWave 2.2s ease-in-out infinite',
                    animationDelay: `${i * 0.06}s`,
                    willChange: 'transform',
                  }}
                >
                   {ch === ' ' ? '\u00A0' : ch}
                </span>
              ))}
            </span>
            {/* Center: Total Photos */}
            <h2 style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              fontSize: '2rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              margin: 0,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              whiteSpace: 'nowrap',
              minWidth: '220px',
            }}>
              Total Photos: {photoFiles.length}
            </h2>
            {/* Right: Countdown Timer */}
            <div style={{
              marginLeft: 'auto',
              width: 64, height: 64, minWidth: 64, minHeight: 64,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}>
              <svg width="64" height="64">
                <circle
                  cx="32" cy="32" r="28"
                  stroke="#ccb4d6"
                  strokeWidth="6"
                  fill="#f8f4ff"
                  // style={{ filter: 'drop-shadow(0 2px 8px #ccb4d633)' }}
                />
                <circle
                  cx="32" cy="32" r="28"
                  stroke="#a4b5ff"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={2 * Math.PI * 28 * (1 - countdown/120)}
                  style={{
                    transition: 'stroke-dashoffset 0.7s cubic-bezier(.4,2,.6,1)',
                    strokeLinecap: 'round',
                    // filter: 'drop-shadow(0 2px 8px #a4b5ff33)'
                  }}
                />
              </svg>
              <div style={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Poppins, Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: countdown <= 15 ? '#ff6b81' : '#764ba2',
                letterSpacing: '1px',
                userSelect: 'none',
                pointerEvents: 'none',
                textShadow: '0 1px 4px #fff8',
                transition: 'color 0.3s',
              }}>
                <span style={{fontSize:'1rem', fontWeight:800, lineHeight:1}}>{formatCountdown(countdown)}</span>
              </div>
            </div>
          </div>
          {showSelection && (
            <p className="selection-info" style={{
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 500,
              fontSize: '1.1rem',
              color: '#6c757d',
              textAlign: 'center',
              marginBottom: '15px',
              lineHeight: 1.4
            }}>
              Pick ur {maxSelect} fave shots for the frame✨
              <span className="selection-count" style={{
                display: 'block',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                color: '#667eea',
                marginTop: '8px',
                fontSize: '1.3rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {selectedPhotos.length}/{maxSelect} selected photos
              </span>
            </p>
          )}

          <div className={`photos-grid ${showSelection ? 'selection-grid' : ''}`}>
            {photoFiles.map((filename, index) => {
              const isSelected = selectedPhotos.includes(filename);
              return (
                <div
                  key={index}
                  className={`photo-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => showSelection ? togglePhotoSelection(filename) : downloadFile(filename)}
                >
                  <img
                    src={joinUrl(API_BASE, `/uploads/${filename}`)}
                    alt={`Foto ${index + 1}`}
                  />
                  {mirroringFiles.has(filename) && (
                    <div className="mirroring-overlay">
                      <div className="mirroring-spinner"></div>
                      <span>Mirroring...</span>
                    </div>
                  )}
                  <div className="photo-info">
                    <span className="photo-number">{index + 1}</span>
                    {isSelected && showSelection && (
                      <div className="selected-indicator">✓</div>
                    )}
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(filename);
                      }}
                      className="download-single-button"
                      disabled={mirroringFiles.has(filename)}
                      style={{display:'none'}}
                    >
                      {mirroringFiles.has(filename) ? 'Mirroring...' : 'Download'}
                    </button> */}
                  </div>
                </div>
              );
            })}
          </div>

          {showSelection && (
            <div className="selection-actions">
              <button
                onClick={proceedToSelection}
                className={`proceed-button ${selectedPhotos.length === maxSelect ? 'active' : 'disabled'}`}
                disabled={selectedPhotos.length !== maxSelect}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  letterSpacing: '0.5px'
                }}
              >
                Frame it! ({selectedPhotos.length}/{maxSelect})
              </button>
            </div>
          )}
        </div>

        {videoFiles.length > 0 && (
          <div className="videos-section" style={{ display: 'none' }}>
            <h2>Video yang Direkam ({videoFiles.length})</h2>
            <div className="videos-grid">
              {videoFiles.map((filename, index) => (
                <div key={index} className="video-item">
                  <video
                    className="mirrored-video"
                    controls
                    preload="metadata"
                    src={joinUrl(API_BASE, `/uploads/${filename}`)}
                    onPlay={(e) => e.currentTarget.classList.add('no-mirror')}
                    onPause={(e) => e.currentTarget.classList.remove('no-mirror')}
                    onLoadedMetadata={(e) => {
                      console.log(`Video ${index + 1} loaded:`, {
                        filename,
                        duration: e.target.duration,
                        videoWidth: e.target.videoWidth,
                        videoHeight: e.target.videoHeight
                      });
                    }}
                    onError={(e) => {
                      console.error(`Error loading video ${index + 1}:`, e.target.error);
                    }}
                  />
                  {mirroringFiles.has(filename) && (
                    <div className="mirroring-overlay">
                      <div className="mirroring-spinner"></div>
                      <span>Mirroring...</span>
                    </div>
                  )}
                  <div className="video-info">
                    <span className="video-number">Video {index + 1}</span>
                    <span className="video-filename">{filename}</span>
                    <button
                      onClick={() => downloadFile(filename)}
                      className="download-single-button"
                      disabled={mirroringFiles.has(filename)}
                    >
                      {mirroringFiles.has(filename) ? 'Mirroring...' : 'Download'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraResults; 